import React, { useState, useContext } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Row, Col, Button, Input, Spinner, Form } from "reactstrap";
import classes from "./LoginForm.module.css";
import axios from "axios";
import "react-toastify/dist/ReactToastify.css";
import { toast, ToastContainer } from "react-toastify";
import { IoLockClosed } from "react-icons/io5";
import { BsAt } from "react-icons/bs";
import GlobalContext from "../../Context/GlobalContext";
import LocalStorage from "../../Utils/localStorageService";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";

function LoginForm() {
  const [userData, setUserData] = useState({ email: "", password: "" });
  const context = useContext(GlobalContext);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const API_URL = process.env.REACT_APP_APIURL;

  const loginMutation = useMutation({
    mutationFn: async (credentials) => {
      return await axios.post(
        `${API_URL}/client/auth/login`,
        credentials,
        {
          timeout: 8000, // 🔥 évite blocage infini
        }
      );
    },

    retry: 1, // 🔥 retry automatique une fois
    retryDelay: 1000,

    onMutate: () => {
      toast.loading(t("logging_in"), { toastId: "login" });
    },

    onSuccess: (response) => {
      toast.dismiss("login");

      const token = response?.data?.token;

      if (!token) {
        toast.error(t("login_generic_error"));
        return;
      }

      // 🔐 stockage token
      LocalStorage.setAccessToken(token);

      // 🔁 update global state
      context?.login?.();

      toast.success(t("login_success"));

      // 🚀 navigation instantanée (PAS DE setTimeout)
      navigate("/", { replace: true });
    },

    onError: (err) => {
      toast.dismiss("login");

      console.error("LOGIN ERROR:", err);

      const responseData = err?.response?.data;

      let errorMsg = t("login_generic_error");

      if (responseData?.message) {
        errorMsg = responseData.message;
      } else if (responseData?.error) {
        errorMsg =
          typeof responseData.error === "string"
            ? responseData.error
            : Object.values(responseData.error).flat()[0];
      } else if (err.code === "ECONNABORTED") {
        errorMsg = t("server_timeout");
      }

      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 5000,
      });
    },
  });

  const onLoginClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // 🔒 anti double click hardcore
    if (loginMutation.isPending) return;

    if (!userData.email || !userData.password) {
      toast.warning(t("fill_all_fields"));
      return;
    }

    loginMutation.mutate(userData);
  };

  return (
    <Row className={classes.formContainer}>
      <ToastContainer position="top-right" newestOnTop />

      <Col xs={12} md={8} className="mx-auto">
        <h4 className={classes.headerTitle}>{t("login")}</h4>

        <Form onSubmit={onLoginClick}>
          <Row>
            <Col xs={12} md={6} className="mx-auto">
              <div className={`mb-3 ${classes.inputWithIcon}`}>
                <Input
                  type="email"
                  value={userData.email}
                  onChange={(e) =>
                    setUserData({ ...userData, email: e.target.value })
                  }
                  className={classes.input}
                  placeholder={t("email_address")}
                  required
                />
                <span className={classes.icon}>
                  <BsAt />
                </span>
              </div>
            </Col>
          </Row>

          <Row>
            <Col xs={12} md={6} className="mx-auto">
              <div className={`mb-3 ${classes.inputWithIcon}`}>
                <Input
                  type="password"
                  value={userData.password}
                  onChange={(e) =>
                    setUserData({ ...userData, password: e.target.value })
                  }
                  className={classes.input}
                  placeholder={t("password")}
                  required
                />
                <span className={classes.icon}>
                  <IoLockClosed />
                </span>
              </div>
            </Col>
          </Row>

          <Row>
            <Col xs={12} md={4} className="mx-auto">
              <div className="d-flex justify-content-center">
                <Button
                  type="submit"
                  className={`btn ${classes.buttonStyle} btn-sm w-100`}
                  disabled={loginMutation.isPending}
                >
                  {loginMutation.isPending ? (
                    <Spinner size="sm" />
                  ) : (
                    t("login")
                  )}
                </Button>
              </div>
            </Col>
          </Row>
        </Form>

        <Row className="mt-4 mb-4">
          <Col xs={12} md={6} className="mx-auto text-center">
            <NavLink to="/forgot-password" style={{ textDecoration: "none" }}>
              <h4 style={{ color: "rgb(130,130,130)", fontSize: "15px" }}>
                {t("forgot_password")}
              </h4>
            </NavLink>
          </Col>
        </Row>

        <Row className="mt-4 mb-4">
          <Col xs={12} md={6} className="mx-auto text-center">
            <h4 style={{ color: "rgb(130,130,130)", fontSize: "15px" }}>
              {t("no_account")}{" "}
              <NavLink to="/signup" style={{ textDecoration: "none" }}>
                {t("signup")}
              </NavLink>
            </h4>
          </Col>
        </Row>
      </Col>
    </Row>
  );
}

export default LoginForm;